/**
 * 10.8F — PERFIL PÚBLICO + RANKING (Supabase, tabela `profiles`).
 *
 * Guarda o NOME do jogador e suas stats de placar (profundidade, nível, poder, abates, vitórias
 * PvP) numa linha por usuário. Todos LEEM (ranking público); cada um só ESCREVE a própria linha
 * (RLS). Tolerante a falha: sem internet/config, o nome fica só local e o ranking abre vazio.
 *
 * Requer a tabela `profiles` no Supabase (SQL à parte, com RLS).
 */
import { supabase } from "./supabase";

const NAME_KEY = "vh_player_name";
const MAX_NAME = 16;
let cachedName: string | null = null;

/** Nome local (fonte de verdade da UI). "" se ainda não escolheu. */
export function getName(): string {
  if (cachedName === null) {
    try {
      cachedName = localStorage.getItem(NAME_KEY) ?? "";
    } catch {
      cachedName = "";
    }
  }
  // 10.9FG — BUG "cacadores sem nome": quem nunca digitou nome no Ranking aparecia como "Cacador"
  // no PvP/ranking/temporada. Sem nome de perfil, vale o NOME DO PERSONAGEM.
  return cachedName || fallbackName;
}
let fallbackName = "";
/** 10.9FG — nome do personagem ativo (usado quando o jogador nao escolheu nome de perfil). */
export function setFallbackName(n: string): void { fallbackName = sanitizeName(n || ""); }

/** Limpa e limita o nome (sem espaços nas pontas, máx 16). */
export function sanitizeName(raw: string): string {
  return raw.replace(/\s+/g, " ").trim().slice(0, MAX_NAME);
}

export function setNameLocal(name: string): void {
  cachedName = sanitizeName(name);
  try {
    localStorage.setItem(NAME_KEY, cachedName);
  } catch {
    /* ignora */
  }
}

/** 10.9EO — exportado: a RAID identifica o jogador pelo mesmo id da nuvem (o servidor confere o poder no perfil). */
export async function cloudUserId(): Promise<string | null> { return uid(); }

async function uid(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) return data.session.user.id;
    const r = await supabase.auth.signInAnonymously();
    return r.data.user?.id ?? null;
  } catch {
    return null;
  }
}

export interface ProfileStats {
  depth: number;
  level: number;
  power: number;
  kills: number;
  pvpWins?: number;
  /** 10.9L28 — personagem (classe) que está jogando: o ranking de Profundidade/Poder é POR PERSONAGEM. */
  classId?: string;
  charName?: string;
}

/** Sobe o perfil (nome + stats) pra nuvem. Chamar ao abrir o ranking e ao salvar.
 *  10.9K4 — AUDITORIA: antes era um upsert direto (a RLS só conferia de quem era a linha, nunca o
 *  valor — dava pra gravar qualquer poder/profundidade/abate no ranking de sempre). Agora passa
 *  pela função `sync_profile` (supabase/profile_integrity.sql), que segura o RITMO de nível,
 *  profundidade e poder. Abates e vitórias PvP nem vão mais daqui: o servidor soma o que a
 *  temporada aceitou (syncSeason logo abaixo). */
export async function syncProfile(stats: ProfileStats): Promise<void> {
  const id = await uid();
  if (!id) return;
  const name = getName() || "Caçador";
  try {
    const { error } = await supabase.rpc("sync_profile", {
      p_name: name,
      p_depth: Math.max(1, Math.min(2_000_000_000, Math.floor(stats.depth))),
      p_level: Math.max(1, Math.min(2_000_000_000, Math.floor(stats.level))),
      p_power: Math.max(0, Math.floor(stats.power)),
    });
    if (error) console.warn("[profile] sync erro:", error.message);
  } catch (e) {
    console.warn("[profile] sync exceção:", e);
  }
  // 10.9L28 — o personagem ATIVO também ganha linha própria (profile_chars): antes só existia 1 linha
  // por LOGIN e a 2ª classe da mesma conta nunca aparecia no ranking. Falha aqui nunca derruba o resto
  // (banco sem a migração ainda = só avisa no console).
  if (stats.classId) {
    try {
      const { error } = await supabase.rpc("sync_profile_char", {
        p_class: stats.classId,
        p_name: (stats.charName || "").trim() || name,
        p_depth: Math.max(1, Math.min(2_000_000_000, Math.floor(stats.depth))),
        p_level: Math.max(1, Math.min(2_000_000_000, Math.floor(stats.level))),
        p_power: Math.max(0, Math.floor(stats.power)),
      });
      if (error) console.warn("[profile] sync personagem erro:", error.message);
    } catch (e) {
      console.warn("[profile] sync personagem exceção:", e);
    }
  }
  // 10.9BA — a temporada anda junto com o perfil (import tardio evita ciclo)
  void import("./CloudSeason").then((m) => m.syncSeason(stats));
}

/** 10.9L28 — personagem excluído sai do ranking (a linha em profile_chars era eterna). Tolerante a falha. */
export async function deleteProfileChar(classId: string): Promise<void> {
  if (!classId || !(await uid())) return;
  try {
    const { error } = await supabase.rpc("delete_profile_char", { p_class: classId });
    if (error) console.warn("[profile] excluir personagem erro:", error.message);
  } catch (e) {
    console.warn("[profile] excluir personagem exceção:", e);
  }
}

// 10.9H — DIVISÕES de PvP por vitórias (identidade competitiva; client-side)
export interface PvpRank {
  name: string;
  color: string;
  icon: string;
}
const PVP_RANKS: { min: number; name: string; color: string; icon: string }[] = [
  { min: 0, name: "Ferro", color: "#8a8f9e", icon: "◈" },
  { min: 3, name: "Bronze", color: "#c87f4a", icon: "◈" },
  { min: 8, name: "Prata", color: "#c9d4e6", icon: "◈" },
  { min: 20, name: "Ouro", color: "#ffd54a", icon: "◈" },
  { min: 40, name: "Platina", color: "#5fe0d0", icon: "❖" },
  { min: 80, name: "Diamante", color: "#62e8ff", icon: "◆" },
  { min: 150, name: "Mestre", color: "#c88bff", icon: "★" },
];
export function pvpRank(wins: number): PvpRank {
  let r = PVP_RANKS[0];
  for (const t of PVP_RANKS) if (wins >= t.min) r = t;
  return { name: r.name, color: r.color, icon: r.icon };
}

export type RankField = "depth" | "power" | "pvp_wins" | "kills";

export interface RankRow {
  user_id: string;
  name: string;
  depth: number;
  level: number;
  power: number;
  kills: number;
  pvp_wins: number;
  /** 10.9L28 — classe do personagem (abas Profundidade/Poder); null = conta antiga ainda sem personagem sincronizado. */
  class_id?: string | null;
}

export interface RankResult {
  rows: RankRow[];
  myId: string | null;
}

/** Busca o top N do ranking por um critério (desc). `depth` desempata por nível. */
export async function fetchRanking(field: RankField, limit = 50): Promise<RankResult> {
  const myId = await uid();
  // 10.9L28 — Profundidade e Poder listam PERSONAGENS (view ranking_chars); Abates e PvP seguem por conta.
  if (field === "depth" || field === "power") {
    try {
      let vq = supabase.from("ranking_chars").select("user_id,class_id,name,depth,level,power").order(field, { ascending: false });
      if (field === "depth") vq = vq.order("level", { ascending: false });
      const v = await vq.limit(limit);
      if (!v.error) {
        const rows = ((v.data ?? []) as Omit<RankRow, "kills" | "pvp_wins">[]).map((r) => ({ ...r, kills: 0, pvp_wins: 0 }));
        return { rows, myId };
      }
      console.warn("[rank] view indisponível, usando profiles:", v.error.message);
    } catch (e) {
      console.warn("[rank] view exceção, usando profiles:", e);
    }
  }
  try {
    let q = supabase
      .from("profiles")
      .select("user_id,name,depth,level,power,kills,pvp_wins")
      .order(field, { ascending: false });
    if (field === "depth") q = q.order("level", { ascending: false });
    const { data, error } = await q.limit(limit);
    if (error) {
      console.warn("[rank] erro:", error.message);
      return { rows: [], myId };
    }
    return { rows: (data ?? []) as RankRow[], myId };
  } catch (e) {
    console.warn("[rank] exceção:", e);
    return { rows: [], myId };
  }
}
